<template>
  <v-row justify="center">
    <v-dialog
      v-model="isVisible"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      max-width="600"
      persistent
      scrollable
    >
      <v-card>
        <v-toolbar
          flat
          height="80"
          max-height="80"
        >
          <v-avatar
            :color="type === 'receiptTotalsZ' ? 'orange' : 'blue'"
          >
            <v-icon dark>
              {{ action === 'pdf' ? 'mdi-file-pdf-box' : action === 'email' ? 'mdi-email-arrow-right' : 'print' }}
            </v-icon>
          </v-avatar>

          <v-toolbar-title class="pl-3">
            <div class="body-3">
              {{ type === 'receiptTotalsZ' ? $t('Reports.SalesReportReceiptsZ.Title') : $t('Statistics.SalesReportOrders.Title') }}
            </div>

            <div class="text-caption">
              {{ action === 'pdf' ? $t('Common.Button.Download') + ' PDF' : action === 'email' ? $t('Common.Button.EmailSend') : $t('Common.Button.Print') }}
            </div>
          </v-toolbar-title>

          <v-spacer />

          <v-btn
            icon
            @click="onCancelButtonClick"
          >
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-divider />

        <v-card-text class="pa-1">
          <v-form
            ref="dataForm"
            v-model="dataFormValid"
            @submit.prevent
          >
            <v-container fluid>
              <v-row v-if="selectRange">
                <v-col
                  cols="12"
                  sm="6"
                >
                  <v-menu
                    v-model="startDateVisible"
                    :close-on-content-click="false"
                    left
                    max-width="290px"
                    min-width="140px"
                    offset-y
                    style="width: 100%"
                    transition="scale-transition"
                  >
                    <template #activator="{on}">
                      <v-text-field
                        v-model="startDateFmt"
                        :label="$t('Common.Date.PeriodFrom')"
                        :rules="validationRules.dataForm.startDate"
                        clearable
                        filled
                        height="36px"
                        hide-details
                        prepend-inner-icon="event"
                        readonly
                        @blur="onStartDateBlur"
                        v-on="on"
                      />
                    </template>

                    <v-date-picker
                      v-model="startDate"
                      :allowed-dates="allowedStartDates"
                      :locale="$i18n.languages.find(lang => lang.locale === $i18n.locale).code || 'el-GR'"
                      first-day-of-week="1"
                      scrollable
                      @input="onStartDateInput"
                    />
                  </v-menu>
                </v-col>

                <v-col
                  cols="12"
                  sm="6"
                >
                  <v-menu
                    v-model="endDateVisible"
                    :close-on-content-click="false"
                    left
                    max-width="290px"
                    min-width="140px"
                    offset-y
                    style="width: 100%"
                    transition="scale-transition"
                  >
                    <template #activator="{on}">
                      <v-text-field
                        v-model="endDateFmt"
                        :label="$t('Common.Date.PeriodTo')"
                        :rules="validationRules.dataForm.endDate"
                        clearable
                        filled
                        height="36px"
                        hide-details
                        prepend-inner-icon="event"
                        readonly
                        @blur="onEndDateBlur"
                        v-on="on"
                      />
                    </template>

                    <v-date-picker
                      v-model="endDate"
                      :allowed-dates="allowedEndDates"
                      :locale="$i18n.languages.find(lang => lang.locale === $i18n.locale).code || 'el-GR'"
                      first-day-of-week="1"
                      scrollable
                      @input="onEndDateInput"
                    />
                  </v-menu>
                </v-col>
              </v-row>

              <v-row
                v-if="selectType"
                dense
              >
                <v-col cols="12">
                  <v-radio-group
                    v-model="withoutOpenHours"
                    row
                  >
                    <v-radio
                      :label="$t('Common.Date.WithOpenHours')"
                      :value="false"
                    />

                    <v-radio
                      :label="$t('Common.Date.WithoutOpenHours')"
                      :value="true"
                    />
                  </v-radio-group>
                </v-col>
              </v-row>

              <v-row
                v-if="selectStaff"
                dense
              >
                <v-col cols="12">
                  <v-select
                    v-model="staffId"
                    :hint="$t('Common.SelectDateRangeDialog.Field.Staff.Hint')"
                    :items="staffList"
                    :label="$t('Common.SelectDateRangeDialog.Field.Staff.Label')"
                    :prepend-icon="$t('Common.SelectDateRangeDialog.Field.Staff.Icon')"
                    item-text="Fullname"
                    item-value="Id"
                    persistent-hint
                    required
                  >
                    <template #selection="{item}">
                      <v-chip
                        class="pl-0"
                        dark
                        label
                        small
                      >
                        <v-avatar
                          :color="item.Active ? 'success' : !item.Uid ? 'info' : 'grey darken-1'"
                          class="mr-2"
                          tile
                        >
                          <v-icon
                            color="white"
                            small
                          >
                            {{ item.Active ? 'mdi-account-check-outline' : !item.Uid ? 'mdi-account-group' : 'mdi-account-alert-outline' }}
                          </v-icon>
                        </v-avatar>

                        <div>{{ item.Fullname }}</div>
                      </v-chip>
                    </template>

                    <template #item="{item}">
                      <v-list-item-avatar :color="item.Active ? 'success' : !item.Uid ? 'info' : 'grey darken-1'">
                        <v-icon color="white">
                          {{ item.Active ? 'mdi-account-check-outline' : !item.Uid ? 'mdi-account-group' : 'mdi-account-alert-outline' }}
                        </v-icon>
                      </v-list-item-avatar>

                      <v-list-item-content>
                        <v-list-item-title>{{ item.Fullname }}</v-list-item-title>
                        <v-list-item-subtitle
                          v-if="item.Uid"
                          class="grey--text text-caption mt-1"
                        >
                          {{ item.Uid }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </template>
                  </v-select>
                </v-col>
              </v-row>

              <v-row
                v-if="selectGuid"
                dense
              >
                <v-col cols="12">
                  <v-select
                    v-model="guid"
                    :hint="$t('Common.SelectDateRangeDialog.Field.Guid.Hint')"
                    :item-text="(item) => item.alias ? `${item.alias} (${item.hostname})` : item.hostname"
                    :items="guidList"
                    :label="$t('Common.SelectDateRangeDialog.Field.Guid.Label')"
                    :prepend-icon="$t('Common.SelectDateRangeDialog.Field.Guid.Icon')"
                    class="mt-4"
                    item-value="guid"
                    persistent-hint
                  >
                    <template #selection="{item}">
                      <v-chip
                        class="pl-0"
                        dark
                        label
                        small
                      >
                        <v-avatar
                          class="mr-2"
                          color="blue"
                          tile
                        >
                          <v-icon
                            color="white"
                            small
                          >
                            mdi-desktop-classic
                          </v-icon>
                        </v-avatar>

                        <div>
                          {{ item.alias ? `${item.alias} (${item.hostname})` : item.hostname }}
                        </div>
                      </v-chip>
                    </template>
                  </v-select>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-4">
          <v-spacer />

          <v-btn
            class="light-blue--text text--darken-1"
            outlined
            @click="onCancelButtonClick"
          >
            {{ $t('Common.Button.Cancel') }}
          </v-btn>

          <v-btn
            :disabled="isLoading || !dataFormValid"
            :loading="isLoading"
            class="green white--text elevation-0"
            @click="onSaveButtonClick"
          >
            {{ $t('Common.Button.Ok') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>

import AppData from '@/mixins/appdata'
import {clone, dateAddDays, dateSubtractDays} from '@/lib/helper/helper.js'

export default {
  name      : 'ExportTotalsZDialog',
  components: {},
  mixins    : [AppData],
  props     : {
    visible: {
      type   : Boolean,
      default: false
    },

    action: {
      type   : String,
      default: 'print'
    },

    type: {
      type   : String,
      default: ''
    },

    selectType: {
      type   : Boolean,
      default: false
    },

    selectStaff: {
      type   : Boolean,
      default: false
    },

    selectGuid: {
      type   : Boolean,
      default: false
    },

    selectRange: {
      type   : Boolean,
      default: false
    }
  },
  data() {
    return {
      isLoading       : false,
      dataFormValid   : false,
      startDateVisible: false,
      endDateVisible  : false,
      datePeriod      : '',
      startDate       : '',
      startDateFmt    : '',
      endDate         : '',
      endDateFmt      : '',
      staffId         : null,
      guid            : null,
      withoutOpenHours: false,
      validationRules : {
        dataForm: {
          startDate: [
            (v) => this.selectRange && !!v || ''
          ],
          endDate: [
            (v) => this.selectRange && !!v || ''
          ]
        }
      }
    }
  },
  computed: {
    staffList() {
      const staffList = clone(this.appConfig?.POS_USERS || [])
      staffList.unshift({Id: null, Fullname: this.$t('Common.SelectDateRangeDialog.Field.Staff.All')})

      return staffList
    },

    guidList() {
      const list = clone(this.appMachines || [])
      list.unshift({guid: null, hostname: this.$t('Common.SelectDateRangeDialog.Field.Guid.All'), alias: ''})

      return list
    },

    maxDaysAllowed() {
      return 7
    },

    isVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      }
    }
  },

  watch: {
    startDate(newVal) {
      if (this.endDate && newVal) {
        this.datePeriod = newVal + ' 00:00_' + this.endDate + ' 00:00'
      } else {
        this.datePeriod = ''
      }
    },

    endDate(newVal) {
      if (this.startDate && newVal) {
        this.datePeriod = this.startDate + ' 00:00_' + newVal + ' 00:00'
      } else {
        this.datePeriod = ''
      }
    },

    isVisible(newVal) {
      if (newVal) {
        this.init()

        this.$nextTick(() => {
          if (this.$refs.dataForm) this.$refs.dataForm.resetValidation()
        })
      }
    }
  },

  created() {
    // console.log('1. created');
  },

  mounted() {
    // console.log('2. mounted');
  },

  updated() {
    // console.log('3. updated');
  },

  beforeDestroy() {
    // console.log('4. destroyed');
  },

  methods: {
    init(dateStartStr, dateEndStr) {
      const respectWorkingHoursReportZ = this.appConfig?.LOCATION_DATA?.RespectWorkingHoursReportZ ?? true
      this.withoutOpenHours = !respectWorkingHoursReportZ
      this.datePeriod = ''
      this.staffId = null
      this.guid = null
      this.startDateFmt = dateStartStr ? this.formatDate(dateStartStr) ?? '' : ''
      this.endDateFmt = dateEndStr ? this.formatDate(dateEndStr) ?? '' : ''
      this.startDate = dateStartStr ? this.parseDate(this.startDateFmt) : ''
      this.endDate = dateEndStr ? this.parseDate(this.endDateFmt) : ''
    },

    formatDate(date) {
      if (!date) {
        return null
      }

      const [year, month, day] = date.split('-')
      return `${day}-${month}-${year}`
    },

    parseDate(date) {
      if (!date) {
        return null
      }

      const [day, month, year] = date.split('-')
      return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    },

    allowedStartDates: function (date) {
      const currDate = new Date(date).getTime()
      const endDate = this.endDate ? new Date(this.endDate).getTime() : null
      const minDate = this.endDate ? dateSubtractDays(this.endDate, this.maxDaysAllowed - 1) : null
      return endDate ? (currDate <= endDate && currDate >= minDate.getTime()) : true
    },

    allowedEndDates: function (date) {
      const currDate = new Date(date).getTime()
      const startDate = this.startDate ? new Date(this.startDate).getTime() : null
      const maxDate = this.startDate ? dateAddDays(this.startDate, this.maxDaysAllowed - 1) : null
      return startDate ? (currDate >= startDate && currDate <= maxDate.getTime()) : true
    },

    todayDateStr() {
      return this.getLocalISOString(new Date()).slice(0, 10)
    },

    getLocalISOString(date) {
      const offset = date.getTimezoneOffset()
      const offsetAbs = Math.abs(offset)
      const isoString = new Date(date.getTime() - offset * 60 * 1000).toISOString()
      return `${isoString.slice(0, -1)}${offset > 0 ? '-' : '+'}${String(Math.floor(offsetAbs / 60)).padStart(2, '0')}:${String(offsetAbs % 60).padStart(2, '0')}`
    },

    onStartDateBlur() {
      this.startDate = this.parseDate(this.startDateFmt)
    },

    onEndDateBlur() {
      this.endDate = this.parseDate(this.endDateFmt)
    },

    onStartDateInput(event) {
      this.startDateFmt = this.formatDate(event)
      this.startDateVisible = false
    },

    onEndDateInput(event) {
      this.endDateFmt = this.formatDate(event)
      this.endDateVisible = false
    },

    onSaveButtonClick() {
      this.$refs.dataForm.validate()

      if (this.dataFormValid) {
        this.isLoading = true
        const payload = {datePeriod: this.datePeriod, withoutOpenHours: this.withoutOpenHours, staffId: this.staffId, guid: this.guid, action: this.action}
        this.$emit('date-range', payload)
        this.onCancelButtonClick()
      }
    },

    onCancelButtonClick() {
      if (this.$refs.dataForm) this.$refs.dataForm.resetValidation()
      this.init()
      this.isVisible = false
      this.isLoading = false
    }

  }
}
</script>
